import { useState } from 'react';

import type { Rule } from './types.js';

export interface RuleListProps {
  rules: Rule[];
  /** Highlighted because the tester says this one would answer. */
  highlightedId?: string | null;
  onReorder: (from: number, to: number) => void;
  onToggle: (rule: Rule) => void;
  onEdit: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
}

/**
 * The rule list, in evaluation order. Dragging a row is the way to set
 * priority — there is no priority field to type into, because the number only
 * ever means "where in this list". The arrow buttons do the same thing for
 * keyboards and for anything that is not a mouse.
 */
export function RuleList({
  rules,
  highlightedId,
  onReorder,
  onToggle,
  onEdit,
  onDelete,
}: RuleListProps) {
  const [dragging, setDragging] = useState<number | null>(null);

  if (rules.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-800 p-6 text-center text-sm text-slate-500">
        Sin reglas. Todo lo que llegue se contesta con la respuesta por defecto.
      </p>
    );
  }

  return (
    <ol className="grid gap-2" data-testid="rule-list">
      {rules.map((rule, index) => (
        <li
          key={rule.id}
          draggable
          data-testid={`rule-row-${rule.id}`}
          onDragStart={() => setDragging(index)}
          onDragEnd={() => setDragging(null)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (dragging !== null) onReorder(dragging, index);
            setDragging(null);
          }}
          className={[
            'flex flex-wrap items-center gap-3 rounded-lg border bg-slate-900/60 p-3',
            dragging === index ? 'opacity-50' : '',
            rule.id === highlightedId ? 'border-sky-500' : 'border-slate-800',
            rule.enabled ? '' : 'opacity-60',
          ].join(' ')}
        >
          <span className="cursor-grab select-none text-slate-600" aria-hidden="true">
            ⠿
          </span>

          <span className="w-6 text-right text-xs tabular-nums text-slate-500">{index + 1}</span>

          <span className="rounded bg-slate-800 px-2 py-0.5 font-mono text-xs text-sky-300">
            {rule.method}
          </span>

          <span className="flex-1 truncate font-mono text-sm text-slate-200">
            {rule.pathPattern}
          </span>

          <span className="font-mono text-sm text-slate-400">{rule.response.status}</span>

          {rule.response.delayMs ? (
            <span className="font-mono text-xs text-amber-400">+{rule.response.delayMs} ms</span>
          ) : null}

          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={`Subir ${rule.method} ${rule.pathPattern}`}
              disabled={index === 0}
              onClick={() => onReorder(index, index - 1)}
              className="rounded border border-slate-700 px-1.5 text-xs text-slate-400 hover:text-slate-100 disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              aria-label={`Bajar ${rule.method} ${rule.pathPattern}`}
              disabled={index === rules.length - 1}
              onClick={() => onReorder(index, index + 1)}
              className="rounded border border-slate-700 px-1.5 text-xs text-slate-400 hover:text-slate-100 disabled:opacity-30"
            >
              ↓
            </button>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={rule.enabled}
              aria-label={`Activar ${rule.method} ${rule.pathPattern}`}
              onChange={() => onToggle(rule)}
            />
            activa
          </label>

          <button
            type="button"
            onClick={() => onEdit(rule)}
            className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:border-sky-600"
          >
            Editar
          </button>
          <button
            type="button"
            aria-label={`Borrar ${rule.method} ${rule.pathPattern}`}
            onClick={() => onDelete(rule)}
            className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-400 hover:border-rose-700 hover:text-rose-400"
          >
            Borrar
          </button>
        </li>
      ))}
    </ol>
  );
}
